// Push a prospect into the client portal.
//
// Confirms the client (prefilled from the prospect), an optional co-client,
// and an onboarding sequence (Standard | None), then POSTs to
// /api/data/prospects/:id/push-to-portal. The endpoint creates a portal
// household + people (flagged lifecycle='prospect') and calls the portal's
// server-to-server provision endpoint to seed onboarding + invite both
// people to sign the engagement.
//
// On success the parent flips the prospect's portalHouseholdId locally so the
// row's Actions menu shows "Pushed" instead of "Push to portal" (no
// double-push). A 502 means the household was created but portal provisioning
// failed; we surface the error and DON'T mark it pushed, so the staffer can
// retry (the flow is idempotent server-side).

import { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/apiFetch';

// Prospect names often arrive "Last, First" (Salesforce convention). Show staff
// the corrected "First Last" so the prefilled value matches what the client
// will see. The server (push-to-portal) also normalizes on submit as a backstop.
function normalizeName(name) {
  const raw = (name || '').trim();
  if (!raw.includes(',')) return raw;
  const idx = raw.indexOf(',');
  const last = raw.slice(0, idx).trim();
  const first = raw.slice(idx + 1).trim();
  return first ? `${first} ${last}` : last;
}

export default function PushToPortalModal({ prospect, onCancel, onPushed }) {
  const [clientName, setClientName] = useState(normalizeName(prospect?.name));
  const [clientEmail, setClientEmail] = useState(prospect?.email || '');
  const [coClientName, setCoClientName] = useState('');
  const [coClientEmail, setCoClientEmail] = useState('');
  const [sequence, setSequence] = useState('standard'); // 'standard' | 'none'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !submitting) onCancel?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, submitting]);

  const divisionLabel = (prospect?.division || '').toUpperCase();
  const coClientPartial =
    (coClientName.trim() && !coClientEmail.trim()) ||
    (!coClientName.trim() && coClientEmail.trim());
  const canSubmit =
    clientName.trim() && clientEmail.trim() && !coClientPartial && !submitting;

  async function handleSubmit() {
    if (!canSubmit) {
      if (coClientPartial) setError('A co-client needs both a name and an email.');
      else setError('Client name and email are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/data/prospects/${encodeURIComponent(prospect.id)}/push-to-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: clientName.trim(),
          clientEmail: clientEmail.trim(),
          coClientName: coClientName.trim() || undefined,
          coClientEmail: coClientEmail.trim() || undefined,
          sequenceKey: sequence === 'standard' ? 'standard' : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || `Push failed (HTTP ${res.status}).`);
        setSubmitting(false);
        return;
      }
      onPushed?.(data.household_id);
    } catch (err) {
      setError(err?.message || 'Network error. Please try again.');
      setSubmitting(false);
    }
  }

  const labelClass = 'block text-[11px] font-bold uppercase tracking-[0.06em] text-[#888] mb-1.5';
  const fieldClass = 'w-full border border-gray-300 rounded-[6px] px-3 py-2 text-[14px] text-navy focus:outline-none focus:border-bullogic-blue';
  const eyebrowClass = 'text-[12px] font-bold uppercase tracking-wider text-deep-teal mb-2.5';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-[60] p-4 overflow-auto" onClick={() => !submitting && onCancel?.()}>
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.18)] w-[540px] max-w-full my-10" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[17px] font-bold text-navy">Push to portal</div>
            <div className="text-[12px] text-[#888] mt-0.5">
              {prospect?.name}{divisionLabel ? ` · ${divisionLabel} prospect` : ''}
            </div>
          </div>
          <button onClick={() => !submitting && onCancel?.()} className="text-[20px] text-[#bbb] hover:text-navy leading-none px-1">&times;</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-auto">
          <div>
            <div className={eyebrowClass}>Client</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Full name</label>
                <input className={fieldClass} value={clientName} onChange={e => setClientName(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input className={fieldClass} type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} />
              </div>
            </div>
          </div>

          <div>
            <div className={eyebrowClass}>
              Co-client <span className="normal-case text-[#bbb] font-normal tracking-normal">(optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Full name</label>
                <input className={fieldClass} value={coClientName} onChange={e => setCoClientName(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input className={fieldClass} type="email" value={coClientEmail} onChange={e => setCoClientEmail(e.target.value)} />
              </div>
            </div>
            <div className="text-[12px] text-[#999] mt-1.5">Both will be invited and can sign the same engagement letter.</div>
          </div>

          <div>
            <div className={eyebrowClass}>Onboarding sequence</div>
            <div className="space-y-2.5">
              <label className={`flex items-start gap-3 border rounded-[8px] px-3.5 py-3 cursor-pointer ${sequence === 'standard' ? 'border-navy bg-navy/[0.04]' : 'border-gray-200 hover:bg-bone/40'}`}>
                <input type="radio" name="sequence" value="standard" checked={sequence === 'standard'} onChange={() => setSequence('standard')} className="mt-0.5" />
                <div>
                  <div className="text-[14px] font-bold text-navy">Standard onboarding</div>
                  <div className="text-[12px] text-[#666]">Sign engagement + pay, then document organizer, welcome call, plan build.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 border rounded-[8px] px-3.5 py-3 cursor-pointer ${sequence === 'none' ? 'border-navy bg-navy/[0.04]' : 'border-gray-200 hover:bg-bone/40'}`}>
                <input type="radio" name="sequence" value="none" checked={sequence === 'none'} onChange={() => setSequence('none')} className="mt-0.5" />
                <div>
                  <div className="text-[14px] font-bold text-navy">No onboarding (portal access only)</div>
                  <div className="text-[12px] text-[#666]">Just give them portal access. No onboarding steps.</div>
                </div>
              </label>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-rose/10 border border-rose rounded-[6px] text-[13px] text-rose">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <div className="text-[12px] text-[#999] max-w-[300px]">
            Creates a portal household flagged as a prospect. They stay out of your client lists until you mark the engagement.
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => !submitting && onCancel?.()} className="px-4 py-2 text-[14px] font-bold text-[#555] bg-white border border-gray-300 rounded-[6px] hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={!canSubmit} className="px-5 py-2 text-[14px] font-bold text-white bg-navy rounded-[6px] hover:bg-[#001a5e] disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? 'Pushing...' : 'Push to portal'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
